
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from 'react-helmet';

import { Grid, Snackbar, SnackbarCloseReason, Typography } from "@material-ui/core";
import { LinkButton } from "../../components/buttons";
import Footer from "../../components/footer.component";
import { AppLoader } from "../../components/app.loader";
import XLSXInput from "../../components/XLSXInput/XLSXInput";
import { SERVER_API_ORDERS } from "../../redux/api.constants";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { ManageOrdersDialog } from "../../components/manageOrders.dialog/ManageOrdersDialog";

import styles from './styles.module.scss';

const validFileTypes = ['.xlsx', '.csv'];

const fetchLogOut = async () => {
    try {
        await axios.post(`${SERVER_API_ORDERS}/auth/logout`, {
            withCredentials: true
        });

        localStorage.removeItem('accessToken');

    } catch (error) {
        console.error('Error during LogOut:', error);
    }
}

export const ManageOrdersPage = () => {
    const [isAuth, setIsAuth] = React.useState(false);
    const [isOpenManageOrdersPopup, setIsOpenManageOrdersPopup] = React.useState(false);

    const [file, setFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [open, setOpen] = React.useState(false);

    const handleClose = (
        event: React.SyntheticEvent | Event,
        reason?: SnackbarCloseReason,
    ) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpen(false);
    };

    const accessToken = localStorage.getItem('accessToken')

    useEffect(() => {
        const fetchIsAuth = async () => {
            try {
                if (!accessToken) {
                    throw Error("Not Authorized")
                }

                const response = await axios.get(`${SERVER_API_ORDERS}/auth/refresh`, {
                    withCredentials: true
                });

                if (response.data?.accessToken) {
                    localStorage.setItem("accessToken", response.data?.accessToken)
                    setIsAuth(true);
                }


            } catch (error) {
                await fetchLogOut();
                setIsOpenManageOrdersPopup(true);

                console.info('Error during submission:', error);
            } finally {
                setIsLoading(false);
            }
        }

        fetchIsAuth();

    }, []);

    const handleFileSelect = (files: File[]) => {
        const selectedFile = files[0] || null;

        if (selectedFile) {
            const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
            if (!validFileTypes.includes(`.${fileExtension}`)) {
                alert('Invalid file type. Please upload a CSV or XLSX file.');
                return;
            }

            setFile(selectedFile);
        }
    };

    const handleFileRemove = () => {
        setFile(null)
    }


    const handleCloseManageOrdersPopUp = () => {
        setIsOpenManageOrdersPopup(false);
    }

    const onSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!file) {
            alert("Please select a file.");
            return;
        }

        try {
            if (!accessToken) {
                throw new Error("Not Authorized");
            }

            setIsLoading(true)

            const formData = new FormData();
            formData.append("file", file);

            await axios.post(`${SERVER_API_ORDERS}/orders`, formData, {
                withCredentials: true,
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            setOpen(true);
            setFile(null);
        } catch (error) {
            await fetchLogOut();
            window.location.href = "/";
            console.error("Error during submission:", error);
        } finally {
            setIsLoading(false)
        }
    };

    return (
        <React.Fragment>
            <Helmet>
                <title>{"Manage Orders | Eurotech"}</title>
                <meta name="description"
                    content={"Eurotech has been the premier representative for quality and ergonomic design in office chairs since 1979. Our commitment to design, function, and form has resulted in over 1 billion dollars in cumulative sales. See the Eurotech difference for yourself."} />
            </Helmet>
            {isAuth && (
                <Grid container className={styles.wrapper}>
                    <Grid item xs={12} className={styles.content}>
                        <article className={styles.content_wrapper}>
                            <Typography variant="h5" className={styles.subtitle}>
                                ORDER TRACKING
                            </Typography>
                            <Typography variant="h2" className={styles.title}>
                                Manage Orders Efficiently<br /> with Our Admin Tools
                            </Typography>
                            <Typography variant="caption" className={styles.description}>
                                Upload customer order data via CSV or XLSX, track shipments, and<br />ensure seamless delivery management.
                            </Typography>

                            <div className={styles.inputContainer}>
                                <XLSXInput onFilesSelected={handleFileSelect} file={file} onRemoveFile={handleFileRemove} />
                            </div>

                            <LinkButton style={{ marginTop: '60px' }} classes={{ root: styles.link_wrapper }} onClick={onSubmit} disabled={!file}>
                                <div className={styles.link}>
                                    <Typography variant="caption">Submit</Typography>
                                    <ChevronRightIcon className={styles.link_icon} />
                                </div>
                            </LinkButton>

                        </article>
                        {isLoading && <div className={styles.content_loader}><AppLoader /></div>}
                    </Grid>

                    <Footer />
                </Grid>
            )}
            <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={handleClose}
                message="File uploaded successfully!"
            />
            <ManageOrdersDialog setIsAuth={setIsAuth} openManageOrdersPopup={isOpenManageOrdersPopup} onClose={handleCloseManageOrdersPopUp} />
        </React.Fragment>
    )
}